import React, { useState, useMemo } from 'react';
import { Upload, X, CheckCircle, AlertCircle, ArrowRight, Database, FileText, BookOpen, ScrollText, Heart } from 'lucide-react';
import { useData } from '../contexts/DataContext';

export default function MigrationWizard() {
  const data = useData();
  const [step, setStep] = useState('prompt'); // 'prompt' | 'importing' | 'done' | 'error'
  const [result, setResult] = useState(null);

  // Detect what data exists in localStorage
  const localData = useMemo(() => {
    const bg    = localStorage.getItem('vv_brand_guidelines') || '';
    const vocab = JSON.parse(localStorage.getItem('vv_vocabulary') || '[]');
    const favs  = JSON.parse(localStorage.getItem('vv_favorites')  || '[]');
    const logs  = JSON.parse(localStorage.getItem('vv_logs') || '[]');
    let ct = {};
    try { ct = JSON.parse(localStorage.getItem('vv_category_templates') || '{}'); } catch {}
    const activeTemplates = Object.values(ct).filter(v => v?.trim().length > 0).length;
    return { bg: bg.length, vocab: vocab.length, favs: favs.length, logs: logs.length, templates: activeTemplates };
  }, []);

  const handleImport = async () => {
    setStep('importing');
    const res = await data.runMigration();
    if (res.ok) {
      setResult(res);
      setStep('done');
    } else {
      setResult(res);
      setStep('error');
    }
  };

  const handleSkip = () => {
    data.skipMigration();
  };

  if (!data?.migrationNeeded) return null;

  return (
    <div className="migration-overlay">
      <div className="migration-modal" role="dialog" aria-modal="true" data-testid="migration-wizard">

        {/* ── Header ── */}
        <div className="migration-header">
          <div className="migration-icon">
            <Database size={20} />
          </div>
          <div>
            <h2 className="migration-title">Importera dina lokala data</h2>
            <p className="migration-sub">Vi hittade data sparad lokalt i din webbläsare från innan cloud-synket aktiverades.</p>
          </div>
        </div>

        {/* ── Content ── */}
        {step === 'prompt' && (
          <>
            <p className="migration-body">
              Vill du flytta din befintliga data till ditt konto? Den kommer sedan synkroniseras mellan alla dina enheter.
            </p>

            <div className="migration-items">
              {localData.bg > 100 && (
                <MigItem icon={<FileText size={14} />} label="Varumärkesriktlinjer" value="Sparade" />
              )}
              {localData.templates > 0 && (
                <MigItem icon={<BookOpen size={14} />} label="Kategorimals" value={`${localData.templates} aktiva`} />
              )}
              {localData.vocab > 0 && (
                <MigItem icon={<BookOpen size={14} />} label="Vokabulär" value={`${localData.vocab} termer`} />
              )}
              {localData.favs > 0 && (
                <MigItem icon={<Heart size={14} />} label="Favoriter" value={`${localData.favs} sparade`} />
              )}
              {localData.logs > 0 && (
                <MigItem icon={<ScrollText size={14} />} label="Aktivitetsloggar" value={`${Math.min(localData.logs, 200)} poster`} />
              )}
            </div>

            <div className="migration-actions">
              <button
                className="migration-btn migration-btn--primary"
                onClick={handleImport}
                data-testid="migration-import-btn"
              >
                <Upload size={14} />
                Importera till mitt konto
              </button>
              <button
                className="migration-btn migration-btn--ghost"
                onClick={handleSkip}
                data-testid="migration-skip-btn"
              >
                Hoppa över — börja från scratch
              </button>
            </div>
          </>
        )}

        {step === 'importing' && (
          <div className="migration-loading">
            <div className="migration-spinner" />
            <p className="migration-loading-text">Importerar data till Supabase…</p>
            <p className="migration-loading-sub">Det här tar några sekunder.</p>
          </div>
        )}

        {step === 'done' && (
          <>
            <div className="migration-success">
              <CheckCircle size={36} className="migration-success-icon" />
              <h3 className="migration-success-title">Import klar!</h3>
              {result && (
                <p className="migration-success-body">
                  {result.vocab > 0 && `${result.vocab} vokabulärtermer · `}
                  {result.favs > 0 && `${result.favs} favoriter · `}
                  {result.logs > 0 && `${result.logs} loggar `}
                  har importerats till ditt konto.
                </p>
              )}
            </div>
            <button
              className="migration-btn migration-btn--primary"
              onClick={() => data.skipMigration()}
              data-testid="migration-done-btn"
            >
              <ArrowRight size={14} />
              Fortsätt till appen
            </button>
          </>
        )}

        {step === 'error' && (
          <>
            <div className="migration-error-state">
              <AlertCircle size={32} className="migration-error-icon" />
              <p className="migration-error-msg">
                Importen misslyckades: {result?.error || 'okänt fel'}
              </p>
            </div>
            <div className="migration-actions">
              <button className="migration-btn migration-btn--primary" onClick={handleImport}>
                Försök igen
              </button>
              <button className="migration-btn migration-btn--ghost" onClick={handleSkip}>
                Hoppa över
              </button>
            </div>
          </>
        )}

        {/* Close / skip X button */}
        {step === 'prompt' && (
          <button className="migration-close" onClick={handleSkip} data-testid="migration-close-btn" aria-label="Stäng">
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function MigItem({ icon, label, value }) {
  return (
    <div className="migration-item">
      <span className="migration-item-icon">{icon}</span>
      <span className="migration-item-label">{label}</span>
      <span className="migration-item-value">{value}</span>
    </div>
  );
}
